import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Rocket, Check, Star } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const Auth = ({ setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // mode: 'login' | 'register' | 'forgot'
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [brandName, setBrandName] = useState('Snagcart');
  const [logoUrl, setLogoUrl] = useState(null);
  const [whiteLabelSlug, setWhiteLabelSlug] = useState(null);
  const [whiteLabelPlan, setWhiteLabelPlan] = useState(null);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', country: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(null); // null=loading, bool=ready

  const safeNextPath = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get('next') || '').trim();
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return null;
    return raw;
  }, [location.search]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = (params.get('plan') || '').toLowerCase();
    if (['starter', 'growth', 'pro'].includes(plan)) {
      setWhiteLabelPlan(plan);
      setMode('register');
    }
  }, [location.search]);

  React.useEffect(() => {
    const loadBrand = async () => {
      try {
        const parts = location.pathname.split('/').filter(Boolean);
        const slugIndex = parts.indexOf('w');
        if (slugIndex !== -1 && parts[slugIndex + 1]) {
          const slug = parts[slugIndex + 1];
          const res = await fetch(`${API_URL}/api/whitelabel/landing/${slug}`);
          if (res.ok) {
            const cfg = await res.json();
            if (cfg.brand_name) setBrandName(cfg.brand_name);
            if (cfg.logo_url) setLogoUrl(cfg.logo_url);
            setWhiteLabelSlug(slug);
          }
        }
      } catch (e) { /* ignore */ }
    };
    loadBrand();
  }, [location.pathname]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/google/status`);
        const data = res.ok ? await res.json() : { configured: false };
        if (!cancelled) setGoogleConfigured(!!data.configured);
      } catch (e) {
        if (!cancelled) setGoogleConfigured(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('google_error')) {
      toast.error('Google sign-in was cancelled or failed. Please try again.');
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && !acceptTerms) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
    const payload = mode === 'login'
      ? { email: formData.email, password: formData.password }
      : {
          ...formData,
          accept_terms: true,
          whitelabel_slug: whiteLabelSlug,
          ...(whiteLabelPlan ? { whitelabel_plan: whiteLabelPlan } : {}),
          ...(formData.country ? { country: formData.country } : {}),
        };
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        toast.success(mode === 'login' ? 'Welcome back!' : 'Account created successfully!');
        navigate(safeNextPath || '/dashboard');
      } else {
        const detail = data.detail || 'Authentication failed';
        toast.error(response.status === 429 ? 'Too many login attempts. Please wait a few minutes and try again.' : detail);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!googleConfigured) {
      toast.error('Google sign-in is not configured on this server.');
      return;
    }
    // First-party Snagcart OAuth — never call Emergent demo IdP.
    const next = encodeURIComponent(safeNextPath || '/dashboard');
    window.location.href = `${API_URL}/api/auth/google/login?next=${next}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() })
      });
      const data = await response.json();
      if (response.ok) {
        setForgotSent(true);
        toast.success('Reset link sent! Check your inbox.');
      } else {
        toast.error(data.detail || 'Failed to send reset email.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === 'login';
  const ffHead = { fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif", letterSpacing: '-0.025em' };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="auth-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#fbbf0f] p-12">
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(22,19,13,0.06) 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute w-[440px] h-[440px] rounded-full bg-white/30 blur-[110px]" style={{ top: '-12%', left: '-14%' }} />
          <div className="absolute w-[380px] h-[380px] rounded-full bg-[#ef9f00]/30 blur-[110px]" style={{ bottom: '-10%', right: '-10%' }} />
        </div>

        <div className="relative z-10 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_4px_0_0_rgba(22,19,13,0.9)]" style={{ background: '#16130d' }}>
            <Rocket className="w-5 h-5 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
          </div>
          <span className="text-2xl font-extrabold text-[#16130d]" style={ffHead}>{brandName}</span>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="text-4xl xl:text-5xl font-extrabold text-[#16130d] leading-[1.1] mb-5" style={ffHead}>
            Build your whole business with one AI workspace.
          </h2>
          <p className="text-lg text-[#3a3327] font-medium mb-8">
            Websites, marketing, leads, sales and support — all powered by AI, all under one login.
          </p>
          <ul className="space-y-3.5">
            {['Launch a website in minutes', 'Find leads & run AI campaigns', 'Build agents just by chatting', 'White-label ready for agencies'].map((t) => (
              <li key={t} className="flex items-center gap-3 text-[#16130d] font-semibold">
                <span className="w-6 h-6 rounded-full bg-[#16130d] flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#fbbf0f]" strokeWidth={3.5} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <span className="flex">
            {[0, 1, 2, 3, 4].map((i) => (<Star key={i} className="w-4 h-4 fill-[#16130d] text-[#16130d]" />))}
          </span>
          <span className="text-sm font-bold text-[#16130d]">4.9/5</span>
          <span className="text-sm text-[#16130d]/70">Trusted by 12,000+ businesses</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="relative flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Logo / brand */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4 lg:hidden">
              {logoUrl ? (
                <img src={logoUrl} alt={brandName} className="h-8 w-auto" />
              ) : (
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#16130d' }}>
                  <Rocket className="w-5 h-5 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
                </div>
              )}
              <span className="text-2xl font-extrabold text-[#16130d]" style={ffHead} data-testid="auth-logo">{brandName}</span>
            </div>
            <h1 className="text-4xl font-extrabold mb-2 text-[#16130d]" style={ffHead} data-testid="auth-title">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'register' && 'Get Started'}
            {mode === 'forgot' && 'Reset Password'}
          </h1>
          <p className="text-stone-500" data-testid="auth-subtitle">
            {mode === 'login' && 'Sign in to continue building'}
            {mode === 'register' && (
              whiteLabelPlan
                ? `Create your account for the ${whiteLabelPlan.charAt(0).toUpperCase() + whiteLabelPlan.slice(1)} plan`
                : 'Create your account to start building'
            )}
            {mode === 'forgot' && "Enter your email and we'll send you a reset link"}
          </p>
        </div>

        {/* Forgot Password form */}
        {mode === 'forgot' && (
          <div className="rounded-3xl border border-stone-200 bg-white p-8 space-y-6 shadow-[0_24px_60px_-32px_rgba(22,19,13,0.45)]">
            {forgotSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#fdf3ce] border-2 border-[#16130d] flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-[#16130d]" strokeWidth={3} />
                </div>
                <p className="text-[#16130d] font-semibold text-lg">Check your inbox</p>
                <p className="text-stone-500 text-sm">
                  If <strong className="text-[#16130d]">{forgotEmail}</strong> is registered, a reset link has been sent. It expires in 1 hour.
                </p>
                <Button
                  type="button"
                  onClick={() => { setMode('login'); setForgotSent(false); setForgotEmail(''); }}
                  className="w-full bg-[#16130d] text-white hover:bg-black rounded-full h-12 font-bold"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <Label htmlFor="forgot-email" className="text-[#16130d] font-semibold mb-2 block">Email address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="bg-white border-2 border-[#16130d]/12 focus:border-[#16130d] rounded-xl h-12 text-[#16130d] placeholder:text-stone-400"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#16130d] text-white hover:bg-black rounded-full h-12 font-bold transition-transform hover:scale-105"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-stone-500 hover:text-[#16130d] transition-colors inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Sign In
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Login / Register form */}
        {mode !== 'forgot' && (
          <form onSubmit={handleSubmit} className="rounded-3xl border border-stone-200 bg-white p-8 space-y-6 shadow-[0_24px_60px_-32px_rgba(22,19,13,0.45)]" data-testid="auth-form">
            {mode === 'register' && (
              <div>
                <Label htmlFor="name" className="text-[#16130d] font-semibold mb-2 block">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={mode === 'register'}
                  data-testid="name-input"
                  className="bg-white border-2 border-[#16130d]/12 focus:border-[#16130d] rounded-xl h-12 text-[#16130d] placeholder:text-stone-400"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-[#16130d] font-semibold mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
                className="bg-white border-2 border-[#16130d]/12 focus:border-[#16130d] rounded-xl h-12 text-[#16130d] placeholder:text-stone-400"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-[#16130d] font-semibold">Password</Label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    className="text-sm text-[#16130d]/60 hover:text-[#16130d] font-semibold transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                data-testid="password-input"
                className="bg-white border-2 border-[#16130d]/12 focus:border-[#16130d] rounded-xl h-12 text-[#16130d] placeholder:text-stone-400"
              />
            </div>
            {mode === 'register' && (
              <div>
                <Label htmlFor="country" className="text-[#16130d] font-semibold mb-2 block">Country</Label>
                <select
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  data-testid="country-input"
                  className="w-full bg-white border-2 border-[#16130d]/12 focus:border-[#16130d] rounded-xl h-12 text-[#16130d] px-3"
                >
                  <option value="">Select country</option>
                  <option value="US">United States</option>
                  <option value="IN">India</option>
                  <option value="GB">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="SG">Singapore</option>
                  <option value="DE">Germany</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            )}
            {mode === 'register' && (
              <label className="flex items-start gap-3 text-sm text-stone-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 rounded border-stone-300 accent-[#16130d]"
                  data-testid="accept-terms"
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-[#16130d] font-semibold hover:underline">Terms</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#16130d] font-semibold hover:underline">Privacy Policy</a>
                </span>
              </label>
            )}
            <Button
              type="submit"
              disabled={loading || (mode === 'register' && !acceptTerms)}
              data-testid="auth-submit-button"
              className="w-full bg-[#16130d] text-white hover:bg-black rounded-full h-12 font-bold transition-transform hover:scale-105"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
            {googleConfigured !== false && (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-stone-200" />
                  <span className="text-xs uppercase tracking-widest text-stone-400">or</span>
                  <div className="h-px flex-1 bg-stone-200" />
                </div>
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={googleConfigured !== true}
                  data-testid="google-signin-button"
                  className="w-full flex items-center justify-center gap-3 bg-white hover:border-[#16130d]/30 border-2 border-[#16130d]/15 rounded-full h-12 font-semibold text-[#16130d] shadow-[0_3px_0_0_rgba(22,19,13,0.12)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                  </svg>
                  {googleConfigured === null ? 'Checking Google…' : 'Continue with Google'}
                </button>
              </>
            )}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setMode(isLogin ? 'register' : 'login')}
                data-testid="auth-toggle-button"
                className="text-stone-500 hover:text-[#16130d] font-medium transition-colors"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
